import React, { useEffect, useState } from 'react';
import { Book, Mic, Monitor, Search, Zap, Shield, Palette, Music, Headphones, Database, Languages, Check, Play } from 'lucide-react';
import AppTour from './components/AppTour';
import WhatsNewSection from './components/WhatsNewSection';
import HelpModal from './components/HelpModal';
import { APP_VERSION } from './version';

// The front page is always dark; these stand in for the console's theme tokens.
const DARK_TOKENS = {
  textPrimary: 'text-slate-50', textSecondary: 'text-slate-300', textMuted: 'text-slate-500',
  rowHover: 'hover:bg-slate-800', divider: 'border-slate-700',
};

const Bubble = ({ size, startX, startY, delay, duration, color }) => {
  return (
    <div
      className="absolute rounded-full opacity-20 animate-float"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        left: `${startX}%`,
        top: `${startY}%`,
        background: `radial-gradient(circle at 30% 30%, ${color}80, ${color}40)`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    />
  );
};

const LandingPage = ({ onGetStarted }) => {
  const [bubbles, setBubbles] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    // Generate random bubbles
    const colors = [
      '#6366F1', // indigo
      '#8B5CF6', // purple
      '#10B981', // emerald
      '#3B82F6', // blue
      '#EC4899', // pink
      '#14B8A6', // teal
    ];

    const newBubbles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      size: Math.random() * 100 + 50, // 50-150px
      startX: Math.random() * 100,
      startY: Math.random() * 100,
      delay: Math.random() * 5,
      duration: Math.random() * 10 + 10, // 10-20s
      color: colors[Math.floor(Math.random() * colors.length)],
    }));

    setBubbles(newBubbles);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {showTour && <AppTour onClose={() => setShowTour(false)} />}
      <HelpModal kind={showChangelog ? 'changelog' : null} onClose={() => setShowChangelog(false)} dm t={DARK_TOKENS} />
      {/* Animated bubbles */}
      {bubbles.map((bubble) => (
        <Bubble key={bubble.id} {...bubble} />
      ))}

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand-600/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#001023] shrink-0">
                <img
                  src={`${process.env.PUBLIC_URL}/logo512.png`}
                  alt="SleekHear"
                  className="w-full h-[155%] object-cover object-top"
                />
              </div>
              <h1 className="text-3xl font-bold text-slate-50">
                SleekHear
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTour(true)}
                className="px-6 py-3 bg-slate-800 border border-slate-700 text-slate-200 rounded-lg hover:bg-slate-700 font-semibold transition-all transform hover:scale-105"
              >
                Tour the App
              </button>
              <button
                onClick={onGetStarted}
                className="px-6 py-3 bg-brand-600 text-slate-50 rounded-lg hover:bg-brand-500 shadow-lg font-semibold transition-all transform hover:scale-105"
              >
                Get Started
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto text-center">
            <button
              onClick={() => document.getElementById('whats-new')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-600/30 transition-all"
            >
              Version {APP_VERSION} is here · See what's new
            </button>

            <h2 className="text-6xl md:text-7xl font-bold mb-6">
              <span className="text-slate-50">
                Transform Your
              </span>
              <br />
              <span className="text-slate-50">
                Worship Experience
              </span>
            </h2>

            <p className="text-xl text-slate-50 mb-12 max-w-3xl mx-auto leading-relaxed">
              Plan the whole service, show Bible verses and worship songs beautifully, control it from a phone,
              and put it on your livestream. AI voice recognition, smart search and offline support for churches of all sizes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onGetStarted}
                className="px-8 py-4 bg-emerald-600 text-slate-50 rounded-xl hover:bg-emerald-600/90 shadow-lg font-bold text-lg transition-all transform hover:scale-105"
              >
                Launch App →
              </button>
              <button
                onClick={() => setShowTour(true)}
                className="px-8 py-4 bg-slate-800 border border-slate-700 text-slate-50 rounded-xl hover:bg-slate-700 font-bold text-lg transition-all transform hover:scale-105"
              >
                Tour the App
              </button>
            </div>
          </div>
        </section>

        <WhatsNewSection onTour={() => setShowTour(true)} onChangelog={() => setShowChangelog(true)} />

        {/* Features Section */}
        <section id="features" className="px-6 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-bold text-slate-50 mb-4">
                Powerful Features
              </h3>
              <p className="text-slate-50 text-lg">
                Everything you need for modern church presentations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Voice Recognition */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover: transition-all transform hover:scale-105">
                <div className="p-3 bg-brand-600 rounded-lg w-fit mb-4">
                  <Mic className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Voice Recognition</h4>
                <p className="text-slate-50 text-sm">
                  Automatically detect Bible verses as you speak during sermons. Hands-free operation for seamless presentations.
                </p>
              </div>

              {/* AI Song Search */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-brand-600 rounded-lg w-fit mb-4">
                  <Headphones className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">AI Song Search</h4>
                <p className="text-slate-50 text-sm">
                  Speak or sing to find worship songs instantly. AI-powered recognition with 25+ built-in songs and unlimited online access.
                </p>
              </div>

              {/* Manual Search */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-brand-600 rounded-lg w-fit mb-4">
                  <Search className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Smart Search</h4>
                <p className="text-slate-50 text-sm">
                  Intelligent autocomplete with chapter and verse suggestions. Search songs online and add to your library instantly.
                </p>
              </div>

              {/* Live Preview */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-brand-600 rounded-lg w-fit mb-4">
                  <Monitor className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Dual Preview</h4>
                <p className="text-slate-50 text-sm">
                  Staging and live preview system. Review verses and songs before displaying them to your congregation.
                </p>
              </div>

              {/* Song Library */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-emerald-600 rounded-lg w-fit mb-4">
                  <Music className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Song Library</h4>
                <p className="text-slate-50 text-sm">
                  Manage worship songs with online search, manual entry, and line-by-line lyric display for perfect presentations.
                </p>
              </div>

              {/* Verse Library */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-slate-700 rounded-lg w-fit mb-4">
                  <Book className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Verse Library</h4>
                <p className="text-slate-50 text-sm">
                  Save your favorite verses with custom labels. Quick access to frequently used scriptures and recent passages.
                </p>
              </div>

              {/* Offline Support */}
              <div className="bg-slate-800  rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-lg transition-all transform hover:scale-105">
                <div className="p-3 bg-brand-600 rounded-lg w-fit mb-4">
                  <Database className="w-8 h-8 text-slate-50" />
                </div>
                <h4 className="text-xl font-bold text-slate-50 mb-3">Offline Ready</h4>
                <p className="text-slate-50 text-sm">
                  Works without internet with built-in Bible versions and 25+ worship songs. Optional API integration for more content.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* New Features Highlight */}
        <section className="px-6 py-20 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-bold text-slate-50 mb-4">
                Complete Worship Solution
              </h3>
              <p className="text-slate-50 text-lg">
                Everything from Bible verses to worship songs, all in one app
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Bible Features */}
              <div className="bg-slate-800  rounded-xl p-8 border border-slate-700 shadow-lg">
                <div className="p-4 bg-brand-600 rounded-lg w-fit mb-6">
                  <Book className="w-10 h-10 text-slate-50" />
                </div>
                <h4 className="text-2xl font-bold text-slate-50 mb-4">Bible Display</h4>
                <ul className="space-y-3 text-slate-50">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>Voice recognition for hands-free verse lookup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>Smart autocomplete search with suggestions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>Multiple Bible versions (KJV, NIV, ESV, NLT)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>Save favorite verses to library</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                    <span>Recent passages history</span>
                  </li>
                </ul>
              </div>

              {/* Song Features */}
              <div className="bg-slate-800  rounded-xl p-8 border border-slate-700 shadow-lg">
                <div className="p-4 bg-brand-600 rounded-lg w-fit mb-6">
                  <Music className="w-10 h-10 text-slate-50" />
                </div>
                <h4 className="text-2xl font-bold text-slate-50 mb-4">Worship Songs</h4>
                <ul className="space-y-3 text-slate-50">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400">♫</span>
                    <span>AI song search by speaking or singing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400">♫</span>
                    <span>25+ built-in worship songs (offline ready)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400">♫</span>
                    <span>Search unlimited songs online</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400">♫</span>
                    <span>Line-by-line lyric display with navigation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400">♫</span>
                    <span>Build your custom song library</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="px-6 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-4xl font-bold text-slate-50 mb-6">
                  Why Choose SleekHear?
                </h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="p-2 bg-emerald-600 rounded-lg h-fit">
                      <Zap className="w-6 h-6 text-slate-50" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-50 mb-2">Lightning Fast</h4>
                      <p className="text-slate-50">
                        Instant verse lookup and display. No lag, no delays. Your message flows smoothly.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="p-2 bg-brand-600 rounded-lg h-fit">
                      <Palette className="w-6 h-6 text-slate-50" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-50 mb-2">Beautiful Design</h4>
                      <p className="text-slate-50">
                        Modern gradients and glass-morphism effects. Customizable colors and fonts to match your church brand.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="p-2 bg-brand-600 rounded-lg h-fit">
                      <Shield className="w-6 h-6 text-slate-50" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-50 mb-2">Reliable & Safe</h4>
                      <p className="text-slate-50">
                        Staging system prevents accidental displays. Always review before going live.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="p-2 bg-brand-600 rounded-lg h-fit">
                      <Languages className="w-6 h-6 text-slate-50" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-50 mb-2">Multiple Bible Versions</h4>
                      <p className="text-slate-50">
                        Support for KJV, NIV, ESV, NLT, and more. Switch versions on the fly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-slate-800  rounded-3xl p-8 border border-slate-700 shadow-lg">
                  <video
                    className="w-full aspect-video rounded-xl border border-slate-700 shadow-inner"
                    controls
                    autoPlay
                    muted
                    loop
                  >
                    <source src="/demo-video.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="mt-6 text-center">
                    <span className="px-4 py-2 bg-brand-600/20 border border-slate-700 rounded-full text-brand-400 text-sm font-semibold inline-flex items-center gap-1.5">
                      <Play className="w-3.5 h-3.5" /> Application Features Demo
                    </span>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 blur-3xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800  rounded-3xl p-12 border border-slate-700 shadow-lg text-center">
              <h3 className="text-4xl font-bold text-slate-50 mb-4">
                Ready to Elevate Your Worship?
              </h3>
              <p className="text-xl text-slate-50 mb-8">
                Display Bible verses and worship songs with our comprehensive church presentation platform
              </p>
              <button
                onClick={onGetStarted}
                className="px-10 py-5 bg-brand-600 text-slate-50 rounded-xl hover:bg-brand-500 shadow-lg font-bold text-xl transition-all transform hover:scale-105"
              >
                Launch SleekHear Now →
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8 border-t border-slate-700">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-slate-400 text-sm">
              © 2025 SleekHear · Version {APP_VERSION}. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
